import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { logs, type LogEntry } from '../lib/api';
import { ws, type WSMessage } from '../lib/websocket';

export function useLogStream() {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [isLiveTailing, setIsLiveTailing] = useState(false);
  const entriesMapRef = useRef(new Map<string, LogEntry>());
  const lastTimestampRef = useRef(0);

  // Poll endpoint every 30s
  const { data: polledEntries, isLoading } = useQuery({
    queryKey: ['logs'],
    queryFn: () => logs.list({ since: lastTimestampRef.current, limit: 200 }),
    refetchInterval: 30_000,
    select: (data) => data.data || [],
  });

  // Merge polled entries into dedup map
  useEffect(() => {
    if (!polledEntries) return;
    let changed = false;
    for (const entry of polledEntries) {
      if (!entriesMapRef.current.has(entry.id)) {
        entriesMapRef.current.set(entry.id, entry);
        changed = true;
      }
    }
    if (changed) {
      const sorted = Array.from(entriesMapRef.current.values()).sort(
        (a, b) => a.timestamp - b.timestamp
      );
      setEntries(sorted);
      if (sorted.length > 0) {
        lastTimestampRef.current = sorted[sorted.length - 1].timestamp;
      }
    }
  }, [polledEntries]);

  // Subscribe to WebSocket log entries
  useEffect(() => {
    ws.connect();
    ws.subscribeLogs();

    const unsub = ws.subscribe((msg: WSMessage) => {
      if (msg.type === 'subscribed-logs') {
        setIsLiveTailing(true);
        return;
      }

      if (msg.type === 'log-entry' && msg.data) {
        const entry = msg.data as LogEntry;
        if (!entriesMapRef.current.has(entry.id)) {
          entriesMapRef.current.set(entry.id, entry);
          setEntries((prev) => {
            const next = [...prev, entry].sort((a, b) => a.timestamp - b.timestamp);
            lastTimestampRef.current = next[next.length - 1].timestamp;
            return next;
          });
        }
      }
    });

    return () => {
      unsub();
    };
  }, []);

  const clear = useCallback(() => {
    entriesMapRef.current.clear();
    lastTimestampRef.current = Date.now();
    setEntries([]);
  }, []);

  return { entries, isLoading, isLiveTailing, clear };
}
